<template lang="pug">
.theindex-post
  hero(
    :image="(additional.headerPics ||{}).hero || post.largeFeaturedImagePath"
    :headline="post.title.rendered"
  )
  .main-container-small
    .author {{author}}
    .date
      span.published(v-if="post.date") {{ ` ${publishedDate}` }}
      span(v-if="showModifiedDate") &nbsp;&vert;&nbsp;
      span.modified(v-if="showModifiedDate") {{ `${__('Updated')}: ${modifiedDate}` }}
    br
    .social
      a(
        v-if="additional.instagram"
        :href="`https://www.instagram.com/${additional.instagram.replace('@', '')}/`"
        rel="noopener noreferrer"
        target="_blank"
      )
        Icon(name="instagram")
        span {{ additional.instagramAlias || additional.instagram }}
      a(
        v-if="additional.twitter"
        :href="`https://www.twitter.com/${additional.twitter.replace('@', '')}/`"
        rel="noopener noreferrer"
        target="_blank"
      )
        Icon(name="twitter")
        span {{ additional.twitterAlias || additional.twitter }}
      a(
        v-if="additional.facebook"
        :href="`https://www.facebook.com/${additional.facebook.replace('@', '')}/`"
        target="_blank"
        rel="noopener noreferrer"
      )
        Icon(name="facebook")
        span {{ additional.facebookAlias || additional.facebook }}
    Tabs(v-model="activeTab" :tabs="tabs")
    .content(v-show="activeTab === 'Bio'")
      p(v-for="p in additional.bioInfo.bio") {{ __(p) }}
    .content.statistics(v-show="activeTab === 'Statistics'")
      .stats(v-if="additional.personalStats")
        h4 {{ __('Personal Stats', 'index') }}
        table(v-if="additional.personalStats")
          tbody
            tr(
              v-for="(value, key) in additional.personalStats"
              :key="`personalStats${key}`"
            )
              td {{ key }}
              td {{ value }}
      .stats(v-if="additional.barbellStats")
        h4 {{ __('Barbell Stats', 'index') }}
        table
          tbody
            tr(
              v-for="(value, key) in additional.barbellStats"
              :key="`barbellStats${key}`"
            )
              td {{ key }}
              td {{ value }}
      .stats(v-if="additional.wodTimes")
        h4 {{ __('WOD Times', 'index') }}
        table
          tbody
            tr(
              v-for="(value, key) in additional.wodTimes"
              :key="`wodTimes${key}`"
            )
              td {{ key }}
              td {{ value }}
    .content.career-highlights(v-show="activeTab === 'Career Highlights'")
      table
        tbody
          tr(
            v-for="year in careerKeys"
            :key="`careerHighlights${year}`"
          )
            td {{ year }}
            td
              .event(v-for="(_, event) in additional.careerHighlights[year]") {{ event }}
            td
              .finish(v-for="finish in additional.careerHighlights[year]") {{ finish }}
  .main-container
    .featured-products-container(v-if="showFavoriteGear")
      h2 {{ `${additional.firstName}'s ${__('Favorite Gear')}` }}
      ProductCarousel(:category="featuredProductsUrl" :groupId="constructorGroupId")
    client-only(v-if="relatedPosts.length")
      .related-posts
        h2 {{ __('Related Index Content') }}
        Carousel
          IndexCard(
            v-for="post in relatedPosts"
            :key="`related-post-${post.id}`"
            :post="post"
            show-category
          )
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import type { ITheIndexPost } from '../types'
import hoverCard from '@/js/pages/category/components/hover-card.vue'
import hero from '@/js/pages/theindex/components/hero.vue'
import { loadPosts } from '@/js/pages/theindex/helpers/search'
import IndexCard from '@/js/pages/theindex/components/index-card.vue'
import metadata from '@/js/helpers/metadata'
import { useRogue } from '@/js/services/Rogue'
import { formatDate } from '@/js/helpers/date'
import { THE_INDEX_TITLES_NS } from '../constants'

const props = defineProps({
  post: {
    type     : Object,
    required : true,
  },
  authorList: {
    type    : Array,
    default : () => ([]),
  },
})

const rogue = useRogue()
const route = useRoute()

const activeTab = ref('Bio')
const relatedPosts = ref<ITheIndexPost[]>([])

useHead(metadata({
  title       : props.post?.title?.rendered ? rogue.__(props.post.title.rendered, THE_INDEX_TITLES_NS) : rogue.__('The Index'),
  description : props.post?.excerpt?.rendered
    ? rogue.__(props.post.excerpt.rendered, THE_INDEX_TITLES_NS)
    : rogue.__('A knowledge base of product and movement information, lifestyle content and strength history.'),
  image  : props.post?.featuredImagePath || '/theindex/2023%20Headers/new%20-%202000%20x%20300/home_ia101k.jpg',
  robots : 'all',
  url    : rogue.localizedBaseURL + route.path,
}, rogue))

const additional = computed(() => {
  try {
    return JSON.parse(props.post?.customJs || '{}') || {}
  } catch (e) {
    // Malformed customJs from WordPress shouldn't break the page
    rogue.log.error(`[athlete] Invalid customJs JSON for post ${props.post?.id}`, e)

    return {}
  }
})

const careerKeys = computed<string[]>(() => Object.keys(additional.value.careerHighlights || {})
  .sort((first, last) => Number(last) - Number(first)))

const tabs = computed(() => {
  const tabs = []

  if (additional.value.bioInfo?.bio) tabs.push('Bio')
  if (additional.value.barbellStats || additional.value.personalStats || additional.value.wodTimes) tabs.push('Statistics')
  if (additional.value.careerHighlights) tabs.push('Career Highlights')

  return tabs
})

const featuredProductsUrl = computed(() => `/athletes/${additional.value.categorySlug || route.params.slug}`)
const constructorGroupId = computed<string | undefined>(() => additional.value.categoryId?.toString())
  
const showFavoriteGear = computed<boolean>(() => {
  if (additional.value.hideProducts === true) return false
  const useConstructor = rogue.featureFlags.isOn('enable_constructor')

  return useConstructor ? !!constructorGroupId.value : !!featuredProductsUrl.value
})

const author = computed<string | null>(() => {
  const name = props.post._embedded?.author?.[0]?.name
  if (!name || !props.authorList.length) return null
  const lower = name.toLowerCase()

  return props.authorList.some(auth => lower.includes(auth)) ? '-' + name : null
})

const publishedDate = computed<string>(() => formatDate(props.post.date, rogue))
const modifiedDate = computed<string>(() => formatDate(props.post.modified, rogue))
const showModifiedDate = computed<boolean>(() => publishedDate.value !== modifiedDate.value)

async function load() {
  relatedPosts.value = await loadPosts(rogue, { search: props.post.title.rendered, exclude: props.post.id }, { perPage: 8 })
}

onBeforeMount(() => {
  load()
})
</script>

<style lang="scss" scoped>
.hero, .content, .featured-products-container {
  margin-bottom: $space-xxxl;
}

.tab-container {
  margin-bottom: 40px;
}

.product-card {
  width: 308px;
}

h2 {
  border-bottom: 1px solid $color-gray-light;
  padding-bottom: 8px;
}

.social {
  margin-bottom: $space-s;
  a {
    display: inline-flex;
    align-items: center;
    font-size: $size-xs;
    color: $color-black;
    text-decoration: underline;

    &:not(:last-child) {
      margin-right: $space-s;
    }
  }

  .icon {
    width: 25px;
    height: 25px;
    margin-right: $space-xxxs;
  }
}

.related-posts .card {
  width: 410px;
  max-width: 100vw;
  margin-right: $space-s;
}

.career-highlights {
  .event, .finish {
    line-height: 1.1;

    &:not(:last-child) {
      margin-bottom: $space-xxs;
    }
  }
}

.statistics {
  .stats {
    &:not(:last-child) {
      margin-bottom: $space-l;
    }
  }

  table {
    width: 100%;
    max-width: 400px;
  }

  td {
    text-align: left;
    width: 50%;
  }
}

.date{
  font-size: 13x;
  color: $color-gray-x-dark;

  .published, .modified {
    font-style: italic;    
  }
}
.author{
  font-size: $size-xs;
  font-style: italic;
}
</style>
